/*
  bloom-variant-availability.js — cooperative safety net for the Bloom PDP.

  Two jobs, both driven off an authoritative variant map (rendered by
  bloom-main-product.liquid as #BloomVariants-<section>):

  1. Add-to-cart truth: after any variant change it recomputes the selected
     variant's availability from the map and sets EVERY add button (main +
     sticky bar) enabled/disabled accordingly. It runs on the native `change`
     (instant) and again on Dawn's `variantChange` pub/sub (last word, after the
     server re-render) — so the button can never get stuck disabled after
     switching away from a sold-out variant.

  2. Sold-out option badges: it flags option values with no available variant,
     adding an "Out of stock" badge over them. The two axes are judged
     differently on purpose:

       - Colour is the primary axis and is judged UNCONDITIONALLY: a swatch is
         only badged when that colour is sold out in every size. Picking a colour
         is the first thing a shopper does, and badging it because of a size
         chosen a moment ago reads as "this colour is gone" when it isn't.
       - Every other axis (size) is judged against the current selection, so the
         size pills are what actually show which sizes are left in the chosen
         colour. This is the axis the badge belongs on.

     Which is which comes from `data-bloom-axis` on each option group, set by
     bloom-variant-picker.liquid. Re-applied after each re-render.

  Loaded as a classic (non-module) deferred script so it shares the global
  scope with pubsub.js / constants.js (subscribe / PUB_SUB_EVENTS).
*/
(function () {
  var STR = (typeof window !== 'undefined' && window.variantStrings) || {};

  function VariantAvailability(info) {
    this.info = info;
    this.section = info.dataset.section;
    this.data = this.readData();
    if (!this.data.length) return;

    var wrap = info.querySelector('.bloom-pdp__variants');
    this.oosLabel = (wrap && wrap.dataset.oosLabel) || 'Out of stock';
    this.addText = STR.addToCart || 'Add to cart';
    this.soldOutText = STR.soldOut || 'Sold out';
    this.unavailableText = STR.unavailable || 'Unavailable';

    this.pending = false;
    this.schedule = this.schedule.bind(this);

    // Native selection (immediate) — survives picker DOM replacement because it
    // is delegated on the stable product-info element.
    info.addEventListener('change', function (e) {
      var t = e.target;
      if (t instanceof Element && t.matches('input[type="radio"], select')) this.schedule();
    }.bind(this));

    // Dawn's re-render finished — re-apply after the server-rendered picker swaps in.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      var self = this;
      subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
        var sid = evt && evt.data && evt.data.sectionId;
        if (sid != null && String(sid) !== String(self.section)) return;
        self.schedule();
      });
    }

    this.refresh();
  }

  VariantAvailability.prototype.readData = function () {
    var el = document.getElementById('BloomVariants-' + this.section);
    if (!el) return [];
    try {
      var parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  // Coalesce bursts and wait two frames so Dawn's DOM swap settles first.
  VariantAvailability.prototype.schedule = function () {
    if (this.pending) return;
    this.pending = true;
    var self = this;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.pending = false;
        self.refresh();
      });
    });
  };

  VariantAvailability.prototype.selectedValues = function (picker) {
    var groups = picker.querySelectorAll('.product-form__input');
    var values = [];
    for (var i = 0; i < groups.length; i++) {
      var checked = groups[i].querySelector('input[type="radio"]:checked');
      if (checked) {
        values.push(checked.value);
        continue;
      }
      var select = groups[i].querySelector('select');
      values.push(select ? select.value : null);
    }
    return values;
  };

  VariantAvailability.prototype.selectedVariant = function (values) {
    return this.data.find(function (v) {
      return values.every(function (val, i) {
        return val == null || v['option' + (i + 1)] === val;
      });
    });
  };

  // `unconditional` ignores what is selected on the other axes — a value counts as
  // available if ANY variant carrying it is in stock. Used for the colour axis.
  VariantAvailability.prototype.optionAvailable = function (groupIndex, value, values, unconditional) {
    return this.data.some(function (v) {
      if (!v.available || v['option' + (groupIndex + 1)] !== value) return false;
      if (unconditional) return true;
      return values.every(function (val, i) {
        return i === groupIndex || val == null || v['option' + (i + 1)] === val;
      });
    });
  };

  VariantAvailability.prototype.refresh = function () {
    var picker = this.info.querySelector('variant-selects');
    if (picker) {
      var values = this.selectedValues(picker);
      var groups = picker.querySelectorAll('.product-form__input');
      for (var g = 0; g < groups.length; g++) {
        var inputs = groups[g].querySelectorAll('input[type="radio"]');
        if (!inputs.length) continue; // dropdown — no visible badge
        var unconditional = groups[g].getAttribute('data-bloom-axis') === 'color';
        var groupHasOos = false;
        for (var i = 0; i < inputs.length; i++) {
          var oos = !this.optionAvailable(g, inputs[i].value, values, unconditional);
          if (oos) groupHasOos = true;
          this.markOption(inputs[i], oos);
        }
        groups[g].classList.toggle('bloom-has-oos', groupHasOos);
      }
      this.syncButtons(this.selectedVariant(values));
    } else {
      this.syncButtons(null);
    }
  };

  VariantAvailability.prototype.markOption = function (input, oos) {
    input.classList.toggle('bloom-oos', oos);
    var label =
      input.nextElementSibling && input.nextElementSibling.tagName === 'LABEL'
        ? input.nextElementSibling
        : null;
    if (!label) return;
    label.classList.toggle('bloom-oos', oos);

    // Split size ranges ("XS/S" drawn as XS + S) are one variant behind two pills, so
    // they get one badge on their shared wrapper. Two badges on adjacent pills are
    // wider than the pills and overlap into something unreadable.
    var host = (input.closest && input.closest('.bloom-splitgroup')) || label;
    var badge = null;
    for (var c = 0; c < host.children.length; c++) {
      if (host.children[c].classList.contains('bloom-oos-badge')) {
        badge = host.children[c];
        break;
      }
    }
    if (oos && !badge) {
      badge = document.createElement('span');
      badge.className = 'bloom-oos-badge';
      badge.textContent = this.oosLabel;
      host.appendChild(badge);
    } else if (!oos && badge) {
      badge.remove();
    }
  };

  VariantAvailability.prototype.syncButtons = function (variant) {
    var soldOut = !variant || !variant.available;
    var buttons = this.info.querySelectorAll('[name="add"]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var span = btn.querySelector(':scope > span');
      var textTarget = span || btn;
      if (soldOut) {
        btn.setAttribute('disabled', 'disabled');
        if (textTarget) textTarget.textContent = variant ? this.soldOutText : this.unavailableText;
      } else {
        btn.removeAttribute('disabled');
        if (textTarget) textTarget.textContent = this.addText;
      }
    }
  };

  function initFor(info) {
    if (!info || info.__bloomVA) return;
    info.__bloomVA = true;
    info.__bloomVAInstance = new VariantAvailability(info);
  }

  function boot() {
    var nodes = document.querySelectorAll('product-info');
    for (var i = 0; i < nodes.length; i++) initFor(nodes[i]);
  }

  // Handle product swaps / late upgrades too.
  document.addEventListener('product-info:loaded', function (e) {
    initFor(e.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
